import React, { useEffect } from 'react';
import dropin, {
  Dropin,
  paypalCreateOptions,
  venmoCreateOptions
} from 'braintree-web-drop-in';
import getConfig from 'next/config';

import { get } from 'lodash';
import css from './BraintreeDropin.module.scss';

const { publicRuntimeConfig } = getConfig();

export interface BraintreeDropinProps {
  braintreeAccessToken: string;
  onSetInstance: (instance: Dropin | undefined) => void;
  loadAmount: number;
  enhancedDebitOptions: {
    paypalEnabled: boolean;
    venmoEnabled: boolean;
    applePayEnabled: boolean;
    googlePayEnabled: boolean;
    threeDSEnabled: boolean;
  };
}

export default function BraintreeDropin({
  braintreeAccessToken,
  onSetInstance,
  loadAmount,
  enhancedDebitOptions
}: BraintreeDropinProps) {
  // Per ACQ-4114: Need to enable credit cards in lower enironment because all Apple and google pay provided cards are credit
  // Disable this in prod

  const environment = get(publicRuntimeConfig, 'nodeEnv', 'dev');
  const isProd = environment === 'production';
  const googleMerchantId = get(
    publicRuntimeConfig,
    'googleMerchantId',
    'merchant-id-from-google'
  );

  const appleMerchantCapabilities = ['supports3DS', 'supportsDebit'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applePay: any = {
    displayName: 'Greenlight',
    paymentRequest: {
      total: {
        label: 'Greenlight',
        amount: `${loadAmount}`
      },
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: [
        'amex',
        'discover',
        'interac',
        'masterCard',
        'privateLabel',
        'visa'
      ],
      // We recommend collecting billing address information, at minimum
      // billing postal code, and passing that billing postal code with all
      // Apple Pay transactions as a best practice.
      requiredBillingContactFields: ['postalAddress']
    }
  };
  if (isProd) {
    applePay.paymentRequest.merchantCapabilities = appleMerchantCapabilities;
  }

  // Intentionally using `any` as the Typescript types require specifying settings that
  // we *prefer* to manage in the Braintree dashboard. It also forces us to use a string where
  // google explicitly wants a number (googlePayVersion), so we must use an untyped entity here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const googlePay: any = {
    googlePayVersion: 2,
    merchantId: `${googleMerchantId}`,
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice: `${loadAmount}`,
      currencyCode: 'USD'
    },
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          // We recommend collecting and passing billing address information with all Google Pay transactions as a best practice.
          billingAddressRequired: true,
          billingAddressParameters: {
            format: 'FULL'
          },
          allowCreditCards: !isProd // false if prod, true if not prod
        }
      }
    ]
  };

  const paypal: paypalCreateOptions = {
    flow: 'vault',
    amount: `${loadAmount}`,
    currency: 'USD'
  };

  // Intentionally using `any` as the Typescript types require specifying settings that are invalid
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const venmo: any = {
    allowNewBrowserTab: false, // Keep within same tab to preserve session
    allowDesktop: true, // Allow desktop to be used
    paymentMethodUsage: 'multi_use' // Allow this payment to be vaulted and resused
  };

  useEffect(() => {
    dropin.create(
      {
        authorization: braintreeAccessToken,
        container: '#dropin-container',
        card: { cardholderName: { required: true } },
        dataCollector: true,
        threeDSecure: enhancedDebitOptions.threeDSEnabled,
        paymentOptionPriority: [
          'applePay',
          'googlePay',
          'card',
          'paypal',
          'venmo'
        ],
        ...{
          applePay: enhancedDebitOptions.applePayEnabled ? applePay : undefined
        },
        ...{
          googlePay: enhancedDebitOptions.googlePayEnabled
            ? googlePay
            : undefined
        },
        ...{
          paypal: enhancedDebitOptions.paypalEnabled ? paypal : undefined
        },
        ...{
          venmo: enhancedDebitOptions.venmoEnabled ? venmo : undefined
        }
      },
      (createErr, instance) => {
        if (!createErr) onSetInstance(instance);
      }
    );
  }, [
    braintreeAccessToken,
    loadAmount,
    enhancedDebitOptions.applePayEnabled,
    enhancedDebitOptions.googlePayEnabled,
    enhancedDebitOptions.venmoEnabled,
    enhancedDebitOptions.paypalEnabled
  ]);
  return (
    <div
      id="dropin-container"
      className={`${css.braintreeDropin} fs-mask`}
      data-testid="braintree"
    />
  );
}
